import React, { useState, useEffect } from 'react'
import { T, Spinner, Avatar, probColor } from '../ui'
import { getNFLLadder } from '../api'
import { teamNick } from '../plain'

// Ladder Spotter — yardage players whose projection sits well above their line, so climbing to a
// higher rung keeps real probability at a fatter price. Each player shows the model's calibrated
// P(>=) at every rung; the standard rung and the best live "stretch" rung are highlighted.
const MARKETS = [['', 'All'], ['rec_yards', 'Rec yds'], ['rush_yards', 'Rush yds'], ['pass_yards', 'Pass yds']]

function Rung({ r, isStretch }) {
  const pct = Math.round((r.prob || 0) * 100)
  const bg = r.main ? T.greenDim : isStretch ? 'rgba(0,217,126,.22)' : T.card2
  const bd = r.main ? T.green : isStretch ? T.greenMid : T.border
  return (
    <div style={{ flex: '0 0 auto', minWidth: 64, textAlign: 'center', background: bg, border: `1px solid ${bd}`,
      borderRadius: 9, padding: '7px 4px' }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, fontFamily: T.mono }}>{r.line}+</div>
      <div style={{ fontSize: 14, fontWeight: 900, fontFamily: T.mono, color: probColor(r.prob || 0) }}>{pct}%</div>
      {r.main && <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: '0.04em' }}>LINE</div>}
      {isStretch && !r.main && <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: '0.04em' }}>STRETCH</div>}
    </div>
  )
}

function LadderCard({ p, onPlayer }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: '12px 13px', marginBottom: 10 }}>
      <button onClick={() => onPlayer && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, team: p.team, pos: p.pos, market: p.market })}
        style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, background: 'none', border: 'none',
          cursor: 'pointer', color: T.text, textAlign: 'left', padding: 0, marginBottom: 10 }}>
        <Avatar url={p.headshot} name={p.name} team={p.team} size={34} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}
            <span style={{ fontSize: 10.5, color: T.muted, fontWeight: 700, marginLeft: 6 }}>{p.pos} · {p.market}</span></div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>vs {teamNick(p.opp)} · we project <b style={{ color: T.green }}>{p.proj}</b> · climbs <b style={{ color: T.text }}>{p.ladder_steps}</b> rung{p.ladder_steps !== 1 ? 's' : ''} to {p.stretch_line}+</div>
        </div>
      </button>
      <div style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 2 }}>
        {(p.rungs || []).map((r, i) => <Rung key={i} r={r} isStretch={r.line === p.stretch_line && !r.main} />)}
      </div>
    </div>
  )
}

export default function LadderTab({ season = 2026, week = 1, onPlayer }) {
  const [market, setMarket] = useState('')
  // Core = the strict board; Long = more players + 7 rungs on the same recalibrated %. Remembered per device.
  const [long, setLong] = useState(() => { try { return localStorage.getItem('brkr_nfl_ladder_long') === '1' } catch (_) { return false } })
  useEffect(() => { try { localStorage.setItem('brkr_nfl_ladder_long', long ? '1' : '0') } catch (_) {} }, [long])
  const [d, setD] = useState(null)
  useEffect(() => { let a = true; setD(null)
    getNFLLadder(season, week, market, long ? 50 : 30, long).then(r => { if (a) setD(r) }).catch(() => { if (a) setD({ players: [] }) })
    return () => { a = false } }, [season, week, market, long])
  const players = (d && d.players) || []
  return (
    <div style={{ padding: '12px 16px 80px' }}>
      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, marginBottom: 10 }}>
        Upside spots: players projected above their line, if the game script breaks right. % is our chance at each rung, <b style={{ color: T.text }}>recalibrated to how ladders actually hit</b> (from Week 9). Higher rungs are long shots, so a ★ stretch rung still needs a long price to be worth it.
      </div>
      <div style={{ display: 'flex', background: T.card2, border: `1px solid ${T.border2}`, borderRadius: 10, padding: 3, gap: 3, marginBottom: 8 }}>
        {[[false, 'Core ladders'], [true, 'Long ladders']].map(([v, lbl]) => {
          const on = long === v
          return <button key={lbl} onClick={() => setLong(v)} aria-pressed={on} style={{ flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer',
            fontSize: 12, fontWeight: 800, border: 'none', background: on ? T.green : 'transparent', color: on ? '#04140D' : T.muted }}>{lbl}</button>
        })}
      </div>
      {long && (
        <div style={{ fontSize: 11.5, color: T.amber, lineHeight: 1.45, marginBottom: 10 }}>
          Long ladders: more players and higher rungs. Same honest % per rung, so the top rungs are long shots. Only worth it at a long price.
        </div>
      )}
      <div style={{ display: 'flex', gap: 7, marginBottom: 12, flexWrap: 'wrap' }}>
        {MARKETS.map(([k, lbl]) => {
          const on = k === market
          return <button key={k} onClick={() => setMarket(k)} style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
            fontSize: 12, fontWeight: 800, border: `1px solid ${on ? T.green : T.border}`,
            background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>{lbl}</button>
        })}
      </div>
      {!d ? <Spinner /> : !players.length ? (
        <div style={{ fontSize: 12.5, color: T.muted, textAlign: 'center', padding: '28px 0', lineHeight: 1.5 }}>
          No ladder spots on the board yet — they appear once projections are built for the week.
        </div>
      ) : (
        <>
          {d.alt_enabled === false && (
            <div style={{ fontSize: 10.5, color: T.faint, marginBottom: 10, lineHeight: 1.45 }}>
              Showing model probability per rung. Per-rung book prices + edge turn on with the alt-lines feed.
            </div>
          )}
          {players.map(p => <LadderCard key={`${p.gsis_id}:${p.market_key}`} p={p} onPlayer={onPlayer} />)}
        </>
      )}
    </div>
  )
}
